import React, { useState, useEffect, useRef } from 'react';
import { Button, Table, Popconfirm, message, Progress, Modal, Alert } from 'antd';
import { DeleteOutlined, PlayCircleOutlined, UploadOutlined, VideoCameraOutlined, FileImageOutlined, DownloadOutlined, WarningOutlined } from '@ant-design/icons';
import api from '../../api/axios';
import { getStudentKvkkConsents } from '../../api/kvkk';
import useAuthStore from '../../store/authStore';

const formatBytes = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// onUploaded: yükleme başarılı olduğunda haber verir — stajyer ekranındaki
// "Son Yüklenenler" listesinin tazelenmesi için kullanılıyor (opsiyonel).
const StudentMediaTab = ({ studentId, onUploaded }) => {
  const { user } = useAuthStore();
  const canUpload  = ['developer_admin', 'super_admin', 'branch_manager', 'teacher', 'stajyer'].includes(user?.role);
  const canDelete  = ['developer_admin', 'super_admin', 'branch_manager', 'teacher'].includes(user?.role);

  const [media, setMedia]           = useState([]);
  const [loading, setLoading]       = useState(false);
  const [uploading, setUploading]   = useState(false);
  const [progress, setProgress]     = useState(0);
  const [preview, setPreview]       = useState(null); // { url, type }
  const [unapprovedConsents, setUnapprovedConsents] = useState([]);
  const fileInputRef                = useRef();

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/students/${studentId}/videos`);
      setMedia(res.data || []);
    } catch {
      message.error('Medya yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedia();
    getStudentKvkkConsents(studentId)
      .then(res => setUnapprovedConsents((res.data.data || []).filter(c => c.status !== 'active')))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 40 * 1024 * 1024) {
      message.error('Dosya boyutu 40MB sınırını aşıyor.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    const formData = new FormData();
    formData.append('video', file);
    setUploading(true);
    setProgress(0);
    try {
      await api.post(`/students/${studentId}/videos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      message.success('Medya yüklendi.');
      fetchMedia();
      onUploaded?.();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Medya yüklenemedi.');
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Blob+XHR yerine backend'in "?download=1" ile donen
  // Content-Disposition:attachment basligina guvenen basit bir <a href>
  // navigasyonu — auth cookie ile gittigi icin ekstra istek gerekmiyor.
  const handleDownload = (item) => {
    const a = document.createElement('a');
    a.href = item.download_url || `/api/students/${studentId}/videos/${item.id}/stream?download=1`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/students/${studentId}/videos/${id}`);
      message.success('Medya silindi.');
      setMedia(prev => prev.filter(v => v.id !== id));
    } catch {
      message.error('Medya silinemedi.');
    }
  };

  const columns = [
    {
      title: 'Dosya Adı',
      dataIndex: 'original_name',
      render: (name, r) => (
        <div className="flex items-center gap-2">
          {r.type === 'photo'
            ? <FileImageOutlined className="text-purple-500" />
            : <VideoCameraOutlined className="text-blue-500" />}
          <span className="text-sm">{name || `${r.type === 'photo' ? 'fotoğraf' : 'video'}-${r.id}`}</span>
        </div>
      ),
    },
    {
      title: 'Boyut',
      dataIndex: 'file_size',
      width: 90,
      render: formatBytes,
    },
    {
      title: 'Yükleyen',
      dataIndex: 'uploader',
      width: 160,
      render: (v) => <span className="text-sm text-gray-600">{v || '-'}</span>,
    },
    {
      title: 'Tarih',
      dataIndex: 'uploaded_at',
      width: 130,
      render: (v) => <span className="text-sm">{v}</span>,
    },
    {
      title: '',
      key: 'actions',
      width: 160,
      render: (_, r) => (
        <div className="flex gap-1">
          <Button
            size="small"
            icon={<PlayCircleOutlined />}
            onClick={() => setPreview({ url: r.stream_url, type: r.type })}
          >
            {r.type === 'photo' ? 'Görüntüle' : 'Oynat'}
          </Button>
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(r)}
          >
            İndir
          </Button>
          {canDelete && (
            <Popconfirm
              title="Bu medyayı silmek istediğinizden emin misiniz?"
              onConfirm={() => handleDelete(r.id)}
              okText="Sil"
              cancelText="İptal"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      {unapprovedConsents.length > 0 && (
        <Alert
          type="error"
          showIcon
          icon={<WarningOutlined />}
          className="mb-4"
          message="Eksik Onay Var"
          description={`Bu öğrenci için "${unapprovedConsents.map(c => `${c.label} (${c.status === 'rejected' ? 'reddedildi' : 'onaylanmadı'})`).join('", "')}" — veli onayı tamamlanmadığı sürece bu öğrencinin görsel/işitsel kayıtlarını dışarıya (sosyal medya, tanıtım vb.) paylaşmayın.`}
        />
      )}

      {canUpload && (
        <div className="mb-4">
          <input
            ref={fileInputRef}
            type="file"
            accept="video/mp4,video/quicktime,video/x-msvideo,video/x-matroska,video/webm,image/jpeg,image/png,image/webp,image/heic"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <Button
            icon={<UploadOutlined />}
            loading={uploading}
            onClick={() => fileInputRef.current?.click()}
            type="primary"
          >
            Medya Yükle
          </Button>
          {uploading && <Progress percent={progress} size="small" className="mt-2" style={{ maxWidth: 300 }} />}
        </div>
      )}

      <Table
        dataSource={media}
        columns={columns}
        rowKey="id"
        size="small"
        loading={loading}
        pagination={false}
        locale={{ emptyText: 'Henüz medya yüklenmemiş.' }}
      />

      <Modal
        open={!!preview}
        onCancel={() => setPreview(null)}
        footer={null}
        width={800}
        destroyOnClose
        title={preview?.type === 'photo' ? 'Fotoğraf' : 'Video'}
      >
        {preview?.type === 'photo' ? (
          <img
            src={preview.url}
            alt=""
            style={{ width: '100%', borderRadius: 8 }}
          />
        ) : preview && (
          <video
            src={preview.url}
            controls
            autoPlay
            style={{ width: '100%', borderRadius: 8 }}
          />
        )}
      </Modal>
    </div>
  );
};

export default StudentMediaTab;
